import { requireLoginForActionPromise } from "@app/components/login-request";
import { createRequests, ICredentials } from "./requests";
import { AxiosResponse } from "axios";
import Jwt from "jsonwebtoken";
import { User } from "@app/user";
import { IRUser } from "./users/helper-schemas";
import { inject } from "@app/modules";
import { deepEqual } from "@app/utils/common";

const getAccessToken = (): string | undefined => {
	try {
		const credentials = JSON.parse(localStorage.credentials);
		return "" + credentials.accessToken;
	} catch (e) {
		return undefined;
	}
};
const getCredentials = (): ICredentials => {
	if (!localStorage.credentials) {
		throw new Error("credentials not set");
	}
	return JSON.parse(localStorage.credentials);
};

const logoutUser = () => {
	const AuthController = inject("AuthController");
	AuthController.logout().then();
};

const decodeClassroomsAccessToken = (newAccessToken: string): User => {
	const decodedObj = Jwt.decode(newAccessToken);
	return User.createUserInstance(decodedObj as IRUser);
};

const coursesTokensHaveToBeUpdated = (newUser: User, oldUser: User | null) => {
	if (!oldUser) return true;
	const newCourses = newUser.getAccessibleCourseIds();
	const oldCourses = oldUser.getAccessibleCourseIds();
	if (newCourses.isKnown && deepEqual(newCourses, oldCourses)) {
		return false;
	}
	return true;
};

const Requests = createRequests({
	accessTokenKey: "access-token",
	urlPrefix: process.env.REACT_APP_BACKEND_SERVER
		? process.env.REACT_APP_BACKEND_SERVER
		: "",
	numOfSeccondsToRenewTokenBeforeExpiration: 0,
	requireLoginForActionPromise: requireLoginForActionPromise,
	updateAccessToken: () => {
		const AuthController = inject("AuthController");
		const credentials = getCredentials();
		return AuthController.updateAccessToken({
			userId: credentials.userId as number,
			refreshToken: credentials.refreshToken,
		});
	},
	initialConfig: {
		headers: {
			"access-token": getAccessToken(),
		},
	},
	onResponse: (response: AxiosResponse) => {
		const newAccessToken = response.headers["access-token"];
		const refreshToken = response.headers["refresh-token"];
		if (newAccessToken || refreshToken) {
			const credentialsStr = localStorage.getItem("credentials");
			if (!credentialsStr) return;
			const AuthController = inject("AuthController");
			const credentials: ICredentials = JSON.parse(credentialsStr);
			if (newAccessToken) {
				try {
					const user = decodeClassroomsAccessToken(newAccessToken);
					const oldUser = decodeClassroomsAccessToken(
						credentials.accessToken
					);
					credentials.userId = user.id;
					AuthController.updateUserDataAction(user.getRawUser());
					const shouldCoursesTokenBeUpdated = coursesTokensHaveToBeUpdated(
						user,
						oldUser
					);
					if (shouldCoursesTokenBeUpdated) {
						CoursesRequests.sendNewAccessTokenRequest(() =>
							Promise.resolve(null)
						).catch();
					}
				} catch (e) {}
			}
			AuthController.renewCredentials({
				accessToken: newAccessToken || credentials.accessToken,
				refreshToken: refreshToken || credentials.refreshToken,
				userId: credentials.userId,
			});
		}
	},
	logoutUser,
	preRequestHook: args => {
		const loc = inject("getLoc")();
		return {
			...args,
			config: {
				...args.config,
				headers: {
					...args.config.headers,
					"loc-w": loc.website,
					"loc-c": loc.country,
					"loc-l": loc.language,
				},
			},
		};
	},
});

const getCoursesToken = (): string | undefined => {
	try {
		const credentials = JSON.parse(localStorage.coursesCredentials);
		return "" + credentials.accessToken;
	} catch (e) {
		return undefined;
	}
};
const getCoursesCredentials = (): ICredentials => {
	if (!localStorage.coursesCredentials) {
		throw new Error("credentials not set");
	}
	return JSON.parse(localStorage.coursesCredentials);
};

export const CoursesRequests = createRequests({
	accessTokenKey: "access_token",
	urlPrefix: process.env.REACT_APP_COURSES_URL
		? process.env.REACT_APP_COURSES_URL
		: "",
	numOfSeccondsToRenewTokenBeforeExpiration: 0,
	requireLoginForActionPromise: requireLoginForActionPromise,
	updateAccessToken: () => {
		const credentials = getCoursesCredentials();
		const AuthController = inject("AuthController");
		return AuthController.updateCoursesAccessToken({
			userId: credentials.userId,
			refreshToken: credentials.refreshToken,
		});
	},
	initialConfig: {
		headers: {
			access_token: getCoursesToken(),
		},
	},
	logoutUser,
});

window.addEventListener("storage", (e: StorageEvent) => {
	if (e.key !== "credentials") return;
	const accessToken = getAccessToken();
	if (typeof accessToken === "string") {
		Requests.renewAccessToken(accessToken);
	} else {
		Requests.renewAccessToken(undefined);
	}
});

export { Requests };
